import { PAYMENTS_SECTIONS_ROUTES } from '@/models/app-internal/permissions.model';
import { GeoPaymentMethodItem, GeoPaymentMethodTypes } from '@/models/ps-management/ps-management.model';
import router from '@/router/router';
import { useGeoPsManagementStore } from '@/store/ps-management/geo-payment-methods.store';
import { useGlobalPsManagementStore } from '@/store/ps-management/global-payment-methods.store';
import { storeToRefs } from 'pinia';
import { computed, defineComponent, onMounted, ref } from 'vue';

export default defineComponent({
  name: 'AddGeoPaymentMethodsDialog',
  setup() {
    const geoPsManagementStore = useGeoPsManagementStore();
    const {
      closeGeoPaymentMethodsAddingDialog,
      createSingleGeoPaymentMethods,
    } = geoPsManagementStore;
    const {
      geoPaymentMethodList,
      isAddGeoPaymentMethodsDialogVisible,
    } = storeToRefs(geoPsManagementStore);

    const globalPsManagementStore = useGlobalPsManagementStore();
    const { getGlobalPaymentMethodList } = globalPsManagementStore;
    const {
      globalPaymentMethodList,
      isGlobalPaymentMethodListLoading,
    } = storeToRefs(globalPsManagementStore);

    const selectedPaymentMethods = ref<GeoPaymentMethodItem[]>([]);

    onMounted(() => {
      if (!globalPaymentMethodList.value.length) {
        getGlobalPaymentMethodList();
      }
    });

    const getAvailableGlobalPaymentMethods = () => {
      return globalPaymentMethodList.value.filter((globalPaymentMethod) => {
        const isGlobalPaymentMethodEnabled = globalPaymentMethod.vaultEnabled;
        const isGlobalPaymentMethodAlreadyAdded = geoPaymentMethodList.value.some((geoPaymentMethod) => {
          return geoPaymentMethod.type === GeoPaymentMethodTypes.SINGLE &&
            geoPaymentMethod.billingPaymentMethodId === globalPaymentMethod.billingPaymentMethodId;
        });

        return isGlobalPaymentMethodEnabled && !isGlobalPaymentMethodAlreadyAdded;
      });
    };

    const selectedGeoPaymentMethodType = ref<string | null>(null);

    const isSelectedSingleGeoPaymentType = computed<boolean>(
      () => selectedGeoPaymentMethodType.value === GeoPaymentMethodTypes.SINGLE);

    const isProceedButtonDisabled = computed<boolean>(() => {
      return selectedGeoPaymentMethodType.value !== GeoPaymentMethodTypes.MULTI &&
        (isGlobalPaymentMethodListLoading.value || !selectedPaymentMethods.value.length);
    });

    const proceedWithSelectedGeoPaymentMethodType = (): void => {
      if (!selectedGeoPaymentMethodType.value) {
        return;
      }

      const openCreateMultiMethodPage = () => {
        router.push({
          name: PAYMENTS_SECTIONS_ROUTES.CREATE_GEO_MULTI_METHOD_PAGE,
        });
      };

      const methodTypeActionsMapping = {
        multi: () => {
          closeGeoPaymentMethodsAddingDialog();
          openCreateMultiMethodPage();
        },
        single: () => {
          if (!selectedPaymentMethods.value.length) {
            return;
          }

          const billingPaymentMethodIdList = selectedPaymentMethods.value.map((selectedPaymentMethod) => {
            return selectedPaymentMethod.billingPaymentMethodId;
          });

          createSingleGeoPaymentMethods(billingPaymentMethodIdList).then(() => {
            closeGeoPaymentMethodsAddingDialog();
          });
        },
      };

      methodTypeActionsMapping[selectedGeoPaymentMethodType.value]();
    };

    return {
      isAddGeoPaymentMethodsDialogVisible,
      closeGeoPaymentMethodsAddingDialog,
      geoPaymentMethodList,
      globalPaymentMethodList,
      isGlobalPaymentMethodListLoading,
      selectedPaymentMethods,
      getAvailableGlobalPaymentMethods,
      selectedGeoPaymentMethodType,
      proceedWithSelectedGeoPaymentMethodType,
      isProceedButtonDisabled,
      GeoPaymentMethodTypes,
      isSelectedSingleGeoPaymentType,
    };
  },
});
